import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useAppConfiguration } from '../../shared/hook/useAppConfiguration';
import AuthorizedLayout from '../../shared/layout/AuthorizedLayout';
import ToastsSection from './ToastsSection';
import OfferingSection from './offering-section';
import MentorsBanner from '../../components/mentors-banner';
import { useStyles } from './styles';
import { selectUser, useAppDispatch } from '../../../store';
import { RecommendationBox } from '../../shared/recommendationBox';
import LocalStorage from '../../../services/LocalStorage';
import { PRODUCT_RECOMMENDATION_KEY } from '../product-assessment';
import { RecommendationMentorCard } from '../../components/recommendation-mentor-card';
import { addMentor, addOffering, addSectionInfo } from '../../../store/common';
import { IBookingEvent, IMentor } from '../../../types';
import OfferingCard from '../../components/offering-card';
import useGetEvents from '../../shared/hook/use-get-events';
import RebookMentor from './rebook-mentor';
import useRebookMentorData from './use-rebook-mentor-data';

export const Dashboard = () => {
  const { withUnlimitedCredits } = useAppConfiguration();
  const { user } = useSelector(selectUser);
  const [open, setOpen] = useState(true);
  const classes = useStyles({ withUnlimitedCredits });
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { recommendationBox, recommendationType } = useGetEvents();
  const { showRebookMentorCard, rebookOneOnOne } = useRebookMentorData();

  const { showProductAssessment, showRecommendations } = useAppConfiguration();

  useEffect(() => {
    if (
      showProductAssessment &&
      !LocalStorage.getItem(PRODUCT_RECOMMENDATION_KEY)
    ) {
      history.push('/product-assessment');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProductAssessment]);

  const onShowMentor = (mentor: IMentor) => {
    history.replace({
      pathname: window.location.pathname,
      search: `mentorId=${mentor.id.toString()}`,
    });

    dispatch(addMentor({ mentor }));
  };

  const onUpcomingEvent = (offering: IBookingEvent) => {
    history.replace({
      pathname: window.location.pathname,
      search: `offeringId=${offering.id.toString()}`,
    });

    dispatch(addOffering({ offering }));
  };

  const onLearnMore = () => {
    dispatch(addSectionInfo({ sectionInfo: 'events' }));
  };

  return (
    <AuthorizedLayout title={t('Dashboard.title')}>
      <Box className={classes.mainContainer}>
        <Box className={classes.toastsSection}>
          <ToastsSection />
        </Box>

        <Box>
          {showRecommendations && (
            <Box className={classes.recommendationContainer}>
              <RecommendationBox
                open={open}
                onToggle={() => setOpen(!open)}
                header={t('v2.dashboard.recommendations_header_label_title')}
                subTitle={
                  recommendationType === 'MENTOR'
                    ? t(
                        'v2.dashboard.recommendations_header_label_body_1-1call',
                      )
                    : t(
                        'v2.dashboard.recommendations_header_label_body_communitySessions',
                      )
                }
                subTitleCollapsed={t(
                  'v2.dashboard.recommendations_header_label_body_collapsedRecommendationsBox',
                )}
              >
                <Box className={classes.mentorRecommendation}>
                  {recommendationBox?.map((item: IMentor | IBookingEvent) =>
                    recommendationType === 'MENTOR' ? (
                      <RecommendationMentorCard
                        key={item?.id}
                        onShow={() => onShowMentor(item as IMentor)}
                        mentor={item as IMentor}
                      />
                    ) : (
                      <OfferingCard
                        key={item?.id}
                        upcomingEvent={item as IBookingEvent}
                        userCredit={user?.creditBalance}
                        onUpcomingEvent={onUpcomingEvent}
                        hideBanners
                      />
                    ),
                  )}
                </Box>
              </RecommendationBox>
            </Box>
          )}

          <Box className={classes.bottomContainer}>
            <Typography variant="h2">
              {t('v2.dashboard.section_mentorBanner_label_title')}
            </Typography>
            <Box className={classes.oneOnOneBanners}>
              {showRebookMentorCard && <RebookMentor {...rebookOneOnOne} />}
              <MentorsBanner
                size={showRebookMentorCard ? 'small' : 'default'}
              />
            </Box>

            <Typography variant="h2">
              {t('v2.dashboard.section_eventList_label_title')}
            </Typography>
            <Box className={classes.descriptionWrapper}>
              <Typography component="p">
                {t(
                  'v2.dashboard.section_eventList_label_body_description_workshopsAndWebinars',
                )}
                <Typography
                  component="span"
                  variant="body1"
                  className={classes.learnMore}
                  onClick={onLearnMore}
                >
                  {` ${t('v2.global.button_label_learnMore')}`}
                </Typography>
              </Typography>
            </Box>

            <OfferingSection />
          </Box>
        </Box>
      </Box>
    </AuthorizedLayout>
  );
};
